import React, { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { Alert, Props as AlertProps } from './Alert';

type Props = {
  id: string | number;
} & Omit<AlertProps, 'variant'>;

export function useAlert(
  variant: AlertProps['variant'] = 'success',
  context: 'toast' | 'inline' = 'toast',
  position: 'top-right' | 'bottom-right' = 'top-right'
) {
  /**
   * Hooks
   */
  const toast = useToast({
    // Defaults
    position,
  });

  const [isVisible, setIsVisible] = useState(true);
  return React.useCallback(
    ({ id, title, description, isClosable = true, showIcon = true, actions, onClose = () => {} }: Props) => {
      /**
       * Methods
       */
      const handleOnClose = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        setIsVisible(false);
        onClose(e);
      };

      // context is inline
      if (context === 'inline') {
        if (isVisible) {
          return (
            <Alert
              variant={variant}
              title={title}
              description={description}
              isClosable={isClosable}
              showIcon={showIcon}
              actions={actions}
              onClose={handleOnClose}
            />
          );
        }

        return null;
      }

      // otherwise toast
      if (toast.isActive(id)) {
        return null;
      }

      toast({
        id,
        isClosable,
        render: () => (
          <Alert
            variant={variant}
            title={title}
            description={description}
            isClosable={isClosable}
            showIcon={showIcon}
            actions={actions}
            onClose={() => toast.close(id)}
          />
        ),
      });
      return null;
    },
    [variant, context, toast, isVisible]
  );
}
